import ExperimentService from 'business/services/ExperimentService';
import { GetSeedNodeInfo } from 'business/services/IngredientService';
import PlateService from 'business/services/PlateService';
import ProteinFormulationService from 'business/services/ProteinFormulationService';
import ScreenService from 'business/services/ScreenService';
import { TreeService } from 'business/services/TreeService';

import BaseSidePanel from 'components/sidePanel/baseSidePanel';
import { ITabItem } from 'components/tab';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { IDropDownList } from '../../customComponents/CommonComponents/RMDropDownList';
import RMSvgIcon, {
  RMSvgIconProps,
} from '../../customComponents/CommonComponents/RMSvgIcon';
import NodeInfoContainerPanel from './NodeInfoContainerPanel';
import ProjectInfoPanel from './ProjectInfoPanel';
import FolderInfoPanel from './FolderInfoPanel';
import ScreenLotsInfoPanel from './ScreenLotsInfoPanel';
import ExperimentPlateInfoPanel from './ExperimentPlateInfoPanel';
import CrystalInfoPanel from './CrystalInfoPanel';
import RMModal from 'components/modal/RMModal';
import { ExperimentReportsDialogue } from 'components/reports/ExperimentReportsDialogue';
import { GetPlatePadding, showAlertSnackBar } from 'utils/Global';
import { EReportTypes } from 'enums/EIconPanels';
import {
  exportTextScreen,
  ExportToXMLExperimentfromExpId,
  ExportToXMLScreenfromExpId,
} from 'components/exports/XMLFileHelper';
import WellService from 'business/services/WellService';
import { EPlateState } from 'enums/EPlatestate';
import {
  AlertDTO,
  BasePropertyDTO,
  BooleanPropertyDTO,
  ExperimentInfoDTO,
  PlateInfoDTO,
  TreeNodeDTO,
} from 'business/models/Models';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import SynchTripService from 'business/services/SynchrotronTripService';
import { useLicenseAuthorizeCommand } from 'CustomHooks/useLicenseAuthorizeCommand';
import { EUserAction } from 'constants/index';
import { LoadingModal } from 'components/modal';
import FragmentService from 'business/services/FragmentService';
import { useAwaitableModal } from 'CustomHooks/useAwaitableModal';
import ProjectSidePanelContext from './projectSidePanelContext';
import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import RotateCanvasModal from 'components/modal/rotateCanvasModal';
import FragmentInfoPanel from './FragmentInfoPanel';
import { TreeControlState } from 'components/explorer';
import { MultipleFilesPanel } from './MultipleFilesPanel';
import SortScoringWellImages, {
  ImageWellScoresAutoScore,
} from 'components/sortScoringWellImages';
import {
  useFetchAllImagingSettings,
  useUserTopAutoScoreSortSettings,
} from 'redux/hooks/common';
import { MetaDetaExplorerTree } from 'constants/propertyMetaDeta';
import useRMLite from 'CustomHooks/useRMLite';
import ProteinSidePanel from './ProteinSidePanel';
import {
  useGenerateScreenStocksReport,
  useMarkAsNotSeen,
  useRotateCanvas,
} from 'components/Experiment/hooks';
import { isImageNowDisabled } from 'components/customComponents/TreeControl/TreeControlHeader/utils';
import { SearchAndFilterPanel } from './SearchAndFilterPanel';
import NodeInfoGridPanel from './NodeInfoGridPanel/NodeInfoGridPanel';
const loadSVGIconProps = (node): RMSvgIconProps => {
  if (node) {
    const similarNodes = [
      ETreeNodeTypes.ProjectFolder,
      ETreeNodeTypes.ProjectsFolder,
      ETreeNodeTypes.ScreensFolder,
      ETreeNodeTypes.SearchesFolder,
    ];

    if (node.type === ETreeNodeTypes.Protein) {
      const props: RMSvgIconProps = { name: 'Protein' };
      return props;
    }
    if (similarNodes.includes(node.type)) {
      const props: RMSvgIconProps = { name: 'ProjectsFolder', fill: '#CE873A' };
      return props;
    } else {
      let color = '#00000';
      if (node.type === ETreeNodeTypes.Project) {
        color = '#003D8F';
      } else if (node.type === ETreeNodeTypes.Projects) {
        color = '#0059CC';
      }
      const props: RMSvgIconProps = { name: node.type, fill: color };
      return props;
    }
  }
};
type ModalStateType = {
  showModal: boolean;
  body: any;
  heading: string;
  onOk?: () => void;
  isCancelOnly?: boolean;
  noButtonText?: string;
};

const experimentService = new ExperimentService();
const screenService = new ScreenService();

interface ProjectSidePanelProps {
  basePanelNode: any;
  selectedNodes: any[];
  createNewNode: any;
  changeExperimentAndPlateState: (
    nodeToUpdate: any,
    plateState: EPlateState,
    experimentState: string,
    plateIdsToDispense: number[],
  ) => void;
  changePlateBarcode: (nodeToUpdate: any, plateId: number, barcode: string) => void;
  setTreeControlState?: React.Dispatch<React.SetStateAction<TreeControlState>>;
  onRefreshTreeNodeAfterProteinVariantChange: (nodeToUpdate) => void;
  openImageNowModal: () => void;
  showProteinVariantModal: boolean;
  setShowProteinVariantModal: (show: boolean) => void;
  selectedExplorerNode?: TreeNodeDTO;
}

const updatedProperties: BasePropertyDTO[] = [];
const gpService = new GlobalPreferencesService();
const ProjectSidePanel = (props: ProjectSidePanelProps) => {
  const {
    basePanelNode,
    selectedNodes,
    createNewNode,
    changeExperimentAndPlateState,
    changePlateBarcode,
    setTreeControlState,
    onRefreshTreeNodeAfterProteinVariantChange,
    openImageNowModal,
    showProteinVariantModal,
    setShowProteinVariantModal,
    selectedExplorerNode,
  } = props;
  const [selectedBasePanelNode, setSelectedBasePanelNode] = useState(null);
  const [modalStates, setModalStates] = useState<ModalStateType>({
    showModal: false,
    body: <div id="projectsidepanel-modalstate-message">Please Wait</div>,
    heading: 'ROCK MAKER',
    onOk: null,
    isCancelOnly: false,
    noButtonText: 'Cancel',
  });
  const [experimentId, setExperimentId] = useState(0);
  const [reportType, setReportType] = useState<string>('');
  const [experimentData, setExperimentData] = useState(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentOperation, setCurrentOperation] = useState('In Progress');
  const [selectedPlateList, setSelectedPlateList] = useState<PlateInfoDTO[]>([]);
  const [isEditBarcodeAllowed, setIsEditBarcodeAllowed] = useState(false);
  const defaultTopImageWellScoreRelatedValues = useUserTopAutoScoreSortSettings();
  const markAsNotSeen = useMarkAsNotSeen();
  const generateScreenStocksReport = useGenerateScreenStocksReport();
  const rotateCanvas = useRotateCanvas();
  const rmLite = useRMLite();
  const useFetchAllImagingSettingsProfiles = useFetchAllImagingSettings();

  /// select user/default settings
  const [wellScoreSelectedParams, setWellScoreSelectedParams] =
    useState<ImageWellScoresAutoScore>({
      scoreGroup:
        defaultTopImageWellScoreRelatedValues[MetaDetaExplorerTree.TopAutoScoreImageGroup]
          ?.value,
      imageSettings:
        defaultTopImageWellScoreRelatedValues[
          MetaDetaExplorerTree.TopAutoScoreImageSetting
        ]?.value,
      searchAutomatically:
        defaultTopImageWellScoreRelatedValues[
          MetaDetaExplorerTree.TopAutoScoreImageSearchAutomatically
        ]?.value,
      numberOfResultsToshow:
        defaultTopImageWellScoreRelatedValues[
          MetaDetaExplorerTree.TopAutoScoreImageListValue
        ]?.value,
    });

  const isExperimentOrAllPlatesDispensed = useMemo(
    () =>
      (selectedPlateList.length === 0 &&
        basePanelNode?.extra?.plateState >= EPlateState.Dispensed) ||
      (selectedPlateList.length > 0 &&
        selectedPlateList.every((plate) => plate.dispensed === 'True')),
    [selectedPlateList, basePanelNode],
  );

  useEffect(() => {
    if (
      defaultTopImageWellScoreRelatedValues[
        MetaDetaExplorerTree.TopAutoScoreImageSearchAutomatically
      ]?.value != undefined &&
      defaultTopImageWellScoreRelatedValues[
        MetaDetaExplorerTree.TopAutoScoreImageListValue
      ]?.value
    ) {
      setWellScoreSelectedParams((prvState) => ({
        ...prvState,
        searchAutomatically:
          defaultTopImageWellScoreRelatedValues[
            MetaDetaExplorerTree.TopAutoScoreImageSearchAutomatically
          ]?.value,
        numberOfResultsToshow:
          defaultTopImageWellScoreRelatedValues[
            MetaDetaExplorerTree.TopAutoScoreImageListValue
          ]?.value,
        imageSettings:
          defaultTopImageWellScoreRelatedValues[
            MetaDetaExplorerTree.TopAutoScoreImageSetting
          ]?.value,
        scoreGroup:
          defaultTopImageWellScoreRelatedValues[
            MetaDetaExplorerTree.TopAutoScoreImageGroup
          ]?.value,
      }));
    }
  }, [defaultTopImageWellScoreRelatedValues]);

  const { modalContent: awaitableModalContent } = useAwaitableModal();
  const licenseAuthCommand = useLicenseAuthorizeCommand();

  const handleSelectionChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setWellScoreSelectedParams((prvState) => ({
      ...prvState,
      [e.target.name]: e.target.value,
    }));
  };
  const getSortbyWellScoreContent = () => {
    if (!wellScoreSelectedParams.imageSettings || !wellScoreSelectedParams.scoreGroup) {
      return null;
    }

    return (
      <SortScoringWellImages
        projectNodeId={selectedNodes[0].id}
        handleSelectionChange={handleSelectionChange}
        scoringOptionSelectedState={wellScoreSelectedParams}
      />
    );
  };

  const onGenerateScreenStocskReport = async () => {
    await generateScreenStocksReport(experimentId);
    setShowModal(false);
  };

  const exportToXml = async () => {
    const node = selectedNodes ? selectedNodes[0] : null;
    if (
      !node ||
      (node.type !== ETreeNodeTypes.Experiment && node.type !== ETreeNodeTypes.Screen)
    ) {
      return;
    }
    setCurrentOperation('Preparing...');
    setIsLoading(true);
    if (node.type === ETreeNodeTypes.Screen) {
      await ExportToXMLScreenfromExpId(node.extra?.id, node.name);
    } else if (node.type === ETreeNodeTypes.Experiment) {
      await ExportToXMLExperimentfromExpId(node.extra?.id, node.name);
    }
    setIsLoading(false);
  };

  const exportToTextScreen = async () => {
    const node = selectedNodes ? selectedNodes[0] : null;
    if (
      !node ||
      (node.type !== ETreeNodeTypes.Experiment && node.type !== ETreeNodeTypes.Screen)
    ) {
      return;
    }
    setCurrentOperation('Preparing...');
    setIsLoading(true);
    await exportTextScreen(node.name, [], node.extra?.id);
    setIsLoading(false);
  };

  const markExperimentAsNotSeen = async () => {
    setIsLoading(true);
    await markAsNotSeen(selectedNodes, setTreeControlState);
    setIsLoading(false);
  };

  const getProteinNodeInfo = async (type, id) => {
    const data = await new ProteinFormulationService().getProteinNodeInfo(id);
    return (
      <ProteinSidePanel
        nodeMenuDetails={getNodeInfoDetails(data, type)}
        type={type}
        header={'Protein Information'}
        treeNodeId={id}
        onRefreshTreeNodeAfterProteinVariantChange={
          onRefreshTreeNodeAfterProteinVariantChange
        }
        showProteinVariantModal={showProteinVariantModal}
        setShowProteinVariantModal={setShowProteinVariantModal}
      />
    );
  };

  const getSeedNodeInfo = async (type, id) => {
    const data = await GetSeedNodeInfo(id);
    return (
      <NodeInfoContainerPanel
        id="projectsidepanel-seednodeinfo-panel"
        nodeMenuDetails={getNodeInfoDetails(data, type)}
        type={type}
        header={'Seed Information'}
        treeNodeId={id}
      />
    );
  };

  const getScreenNodeInfo = async (type, id) => {
    const data = await new ExperimentService().getExperimentInfoByTreeNodeId(id);
    const content = [];
    content.push(
      <NodeInfoContainerPanel
        id="projectsidepanel-screennodeinfo-nodeinfocontainerpanel"
        nodeMenuDetails={getNodeInfoDetails(data, type)}
        type={type}
        header={'Screen Information'}
        treeNodeId={id}
      />,
    );
    const screenInfo = await new ScreenService().getScreenNodeInfo(id);
    content.push(
      <ScreenLotsInfoPanel
        id="projectsidepanel-screennodeinfo-screenlotsinfopanel"
        screenNodeInfoDTO={screenInfo}
        experimentId={basePanelNode?.extra?.id}
        plates={basePanelNode?.extra.plates}
        isEditBarcodeAllowed={isEditBarcodeAllowed}
      />,
    );
    return content;
  };

  const getFragmentScreenNodeInfo = async (type, id) => {
    const data = await new FragmentService().getFragmentScreenCount(id);
    const content = [];
    content.push(
      <NodeInfoContainerPanel
        id="projectsidepanel-fragmentscreennodeinfo-nodeinfocontainerpanel"
        nodeMenuDetails={getNodeInfoDetails(data, type)}
        type={type}
        header={'Fragment Screen Information'}
        treeNodeId={id}
      />,
    );
    content.push(
      <FragmentInfoPanel
        id="projectsidepanel-fragmentscreennodeinfo-fragmentinfopanel"
        fragmentId={basePanelNode?.extra?.id}
      />,
    );

    return content;
  };

  const getProjectNodeInfo = async (type, id) => {
    const content = [];
    const projectInfoBody = await new TreeService().GetProjectNodeData(id);
    content.push(
      <ProjectInfoPanel
        id="projectsidepanel-projectnodeinfo-projectinfopanel"
        usersList={projectInfoBody?.users}
        selectedUser={projectInfoBody?.node?.ownerUserId}
        imageScheduleList={projectInfoBody?.imagingSchedules}
        selectedImageSchedule={projectInfoBody?.node?.defaultImagingScheduleId}
        createdDate={projectInfoBody?.node?.dateCreated}
        notes={projectInfoBody?.node?.notes}
        projectId={projectInfoBody?.node?.id}
        treeNodeId={projectInfoBody?.treeNodeId}
      />,
    );
    const crystalsInfoBody = await new TreeService().GetProjectCrystalData(id);
    content.push(<CrystalInfoPanel crystalDTO={crystalsInfoBody} />);

    if (!rmLite && selectedNodes?.length === 1 && selectedNodes?.[0].id) {
      content.push(getSortbyWellScoreContent());
    }
    return content;
  };

  const getFolderNodeInfo = async (type, id) => {
    const data = await new TreeService().GetNodeChildsCount(id);
    return <FolderInfoPanel info={data} />;
  };

  const getExperimentNodeInfo = async (type, id, extra) => {
    const data = await new ExperimentService().getExperimentInfoByTreeNodeId(id);
    const content = [];
    const experimentSections = getExperimentGridSections(data);
    setExperimentId(data.id);
    content.push(
      <NodeInfoGridPanel
        header={'Experiment Information'}
        sections={experimentSections}
        columnsPerRow={4}
        ColumnTemplate="30% 25% 25% 20%"
        gap="6px"
        padding="8px"
      />,
    );
    const expPlateInfo = (await new PlateService().getExperimentPlatesInfo(id)).map(
      (item, index) =>
        ({
          ...item,
          dispensed:
            extra?.plates?.[index]?.plateState === EPlateState.Dispensed
              ? 'True'
              : item.dispensed,
          barcode: extra?.plates?.[index]?.barcode,
        } as unknown as PlateInfoDTO),
    );
    content.push(
      <ExperimentPlateInfoPanel
        expDTO={expPlateInfo}
        experimentId={basePanelNode?.extra?.id}
        plates={basePanelNode?.extra.plates}
        isEditBarcodeAllowed={isEditBarcodeAllowed}
      />,
    );

    if (!rmLite && selectedNodes?.length === 1 && selectedNodes?.[0].id) {
      content.push(getSortbyWellScoreContent());
    }

    return content;
  };

  const getExperimentGridSections = (data: ExperimentInfoDTO) => {
    const sections = [
      {
        columnsPerRow: 2,
        items: [
          { labelName: 'Created', info: data.dateCreated },
          { labelName: 'Experiment ID', info: data.id },
        ],
      },
      {
        columnsPerRow: 1,
        items: [{ labelName: 'Owner', info: data.userName }],
      },
      {
        sectionTitle: 'Plate',
        items: [
          { labelName: 'Type', info: data.plateType, span: 3 },
          { labelName: 'Row', info: data.plateRowCount },
          { labelName: 'Col', info: data.plateColCount },
        ],
      },
      {
        sectionTitle: 'Temperature',
        columnsPerRow: 2,
        items: [
          { labelName: 'Setup', info: `${data.setupTemperature} °C` },
          { labelName: 'Incubation', info: `${data.incubTemperature} °C` },
        ],
      },
      {
        items: [{ labelName: 'Reducing Agent', info: data.reducingAgent, span: 3 }],
      },
      ...(data?.proteinInfo?.flatMap((proteinInfo) => [
        {
          sectionTitle: 'Protein',
          columnsPerRow: 2,
          items: [
            { labelName: 'Name', info: proteinInfo.name },
            {
              labelName: 'Volume',
              info: proteinInfo.volume ? `${proteinInfo.volume} µL` : '-',
            },
          ],
        },
        proteinInfo.ingredients?.length && {
          sectionTitle: 'Protein Ingredient',
          columnsPerRow: 2,
          items:
            proteinInfo.ingredients?.flatMap((ingredient) => [
              { labelName: 'Name', info: ingredient.name ?? '-' },
              {
                labelName: 'Concentration',
                info:
                  ingredient.concentration == null
                    ? '-'
                    : `${ingredient.concentration} mg/mL`,
              },
            ]) ?? [],
        },
      ]) ?? []),
      data?.screenInfo?.length
        ? {
            columnsPerRow: 1,
            sectionTitle: 'Screen',
            items: data.screenInfo.map((screen) => ({
              labelName: 'Name',
              info: screen,
              span: 3,
            })),
          }
        : null,
      {
        items: [{ labelName: 'Notebook Page', info: data.noteBookPageNum, span: 3 }],
      },
      {
        items: [{ labelName: 'Notes', info: data.notes, span: 3 }],
      },
    ].filter(Boolean);
    return sections;
  };

  const getNodeInfoDetails = (data, type) => {
    if (type === ETreeNodeTypes.Protein) {
      const nodeInfoDetails = [
        { labelName: 'Date Prepared', info: data?.nodeInfo.dateCreated },
        { labelName: 'Ingredient', info: data?.nodeInfo.proteinFormulationIngredient },
      ];
      return nodeInfoDetails;
    } else if (type === ETreeNodeTypes.Seed) {
      const nodeInfoDetails = [
        { labelName: 'Created', info: data?.dateCreated },
        { labelName: 'From Experiment', info: data?.expName },
        { labelName: 'Well', info: data?.wellPlace },
        { labelName: 'Stabalizing Ing.', info: data?.stabalizingIngredients },
        { labelName: 'Use Well Solution', info: data?.useWellSolution },
      ];
      return nodeInfoDetails;
    } else if (type === ETreeNodeTypes.Experiment) {
      setExperimentId(data.id);
      return getExperimentGridSections(data);
    } else if (type === ETreeNodeTypes.Screen) {
      const nodeInfoDetails = [
        { labelName: 'Created', info: data.dateCreated },
        { labelName: 'Owner', info: data.userName },
        { labelName: 'Plate Type', info: data.plateType },
        {
          labelName: 'Screen ID',
          info: data.id,
        },
        { labelName: 'Plate Rows', info: data.plateRowCount },
        { labelName: 'Plate Columns', info: data.plateColCount },
        { labelName: 'Setup Temp.', info: data.setupTemperature },
        { labelName: 'Incubation Temp.', info: data.incubTemperature },
        { labelName: 'Reducing Agent', info: data.reducingAgent },
        { labelName: 'Notebook Page', info: data.noteBookPageNum },
        { labelName: 'Notes', info: data.notes },
      ];
      setExperimentId(data.id);
      return nodeInfoDetails;
    } else if (type === ETreeNodeTypes.FragmentScreen) {
      const nodeInfoDetails = [
        { labelName: 'Total Conditions', title: 'Total Conditions', info: data },
      ];
      return nodeInfoDetails;
    } else if (type === ETreeNodeTypes.CrystalTrackv4) {
      return [
        { labelName: 'Number of Trips', info: data?.totalTrips },
        {
          labelName: 'Upcoming Trips',
          info: data?.upcomingTrips,
        },
        {
          labelName: 'Past Trips',
          info: data?.pastTrips,
        },
      ];
    }
  };

  const getSynchrotronTripsNodeInfo = async (type, id) => {
    const data = await new SynchTripService().getSynchTripsNodeInfo();
    return (
      <NodeInfoContainerPanel
        nodeMenuDetails={getNodeInfoDetails(data, type)}
        type={type}
        header="Synchrotron Trips Information"
        treeNodeId={id}
      />
    );
  };

  const getTab = async (type, id, extra = null) => {
    if (type === ETreeNodeTypes.Protein) {
      return await getProteinNodeInfo(type, id);
    } else if (type === ETreeNodeTypes.Seed) {
      return await getSeedNodeInfo(type, id);
    } else if (type === ETreeNodeTypes.Experiment) {
      return await getExperimentNodeInfo(type, id, extra);
    } else if (type === ETreeNodeTypes.Screen) {
      return await getScreenNodeInfo(type, id);
    } else if (type === ETreeNodeTypes.FragmentScreen) {
      return await getFragmentScreenNodeInfo(type, id);
    } else if (type === ETreeNodeTypes.Project) {
      return await getProjectNodeInfo(type, id);
    } else if (type === ETreeNodeTypes.CrystalTrackv4) {
      return await getSynchrotronTripsNodeInfo(type, id);
    } else if (type === ETreeNodeTypes.SearchAnalysis) {
      return <SearchAndFilterPanel treeNodeId={id} />;
    } else {
      const folderTypes = [
        ETreeNodeTypes.ProjectFolder,
        ETreeNodeTypes.Screens,
        ETreeNodeTypes.Projects,
        ETreeNodeTypes.ScreensFolder,
        ETreeNodeTypes.ProjectsFolder,
        ETreeNodeTypes.SynchrotronTripv4,
        ETreeNodeTypes.Searches,
        ETreeNodeTypes.SearchesFolder,
      ];
      if (folderTypes.includes(type)) {
        return await getFolderNodeInfo(type, id);
      }
    }
  };

  const exportPoi = async () => {
    const wellService = new WellService();
    const fileResponse = await wellService.generatePoiCsvFile(
      experimentId,
      false,
      null,
      null,
      null,
    );
    const blob = new Blob([fileResponse.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileResponse.fileName;
    link.click();
  };
  const markExperimentAsDispensed = licenseAuthCommand(
    useCallback(async () => {
      setIsLoading(true);
      const plateIdsToDispense: number[] = selectedPlateList?.map((plate) => {
        return plate.id;
      });

      const response: AlertDTO = plateIdsToDispense?.length
        ? await new ExperimentService().markExperimentPlatesAsDispensed(
            plateIdsToDispense,
          )
        : await new ExperimentService().markExperimentAsDispensed(experimentId);
      setIsLoading(false);
      if (response.alertType == EAlertType.ERROR) {
        setModalStates({
          ...modalStates,
          showModal: true,
          body: response.alertMessage,
          heading: response.alertTitle,
          isCancelOnly: true,
          noButtonText: 'OK',
        });
      } else {
        showAlertSnackBar(
          EAlertType.SUCCESS,
          ESnackBarPosition.TOP,
          response.alertMessage,
        );
        changeExperimentAndPlateState(
          basePanelNode,
          EPlateState.Dispensed,
          'Dispensed',
          plateIdsToDispense,
        );
      }
    }, [experimentId, selectedPlateList]),
    EUserAction.MarkAsDispensed,
  );

  const onEditPlateBarcode = (plateId: number, barcode: string) => {
    changePlateBarcode(basePanelNode, plateId, barcode);
  };

  const ExperimentOptions: IDropDownList[] = [
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-experimentoptions-svg-imagenow-icon"
          name="ImageNow"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: 'Image Now',

      onClick: licenseAuthCommand(() => {
        openImageNowModal();
      }, EUserAction.ImageNow),
      isDisabled: isImageNowDisabled(rmLite, selectedNodes),
    },
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-experimentoptions-svg-experimentreport-icon"
          name="ExperimentReport"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: 'Experiment Report',

      onClick: () => {
        setReportType(EReportTypes.ExperimentReport);
      },
      isDisabled: true,
    },
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-experimentoptions-svg-markasnotseen-icon"
          name="MarkAsNotSeen"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: 'Mark as Not Seen',
      onClick: markExperimentAsNotSeen,
      isDisabled:
        rmLite ||
        !selectedNodes.every((node) => node.type === 'Experiment') ||
        selectedNodes.some((node) => !!node.extra?.notSeen) ||
        selectedNodes.some((node) => node.extra?.plateState < EPlateState.Imaged),
    },
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-experimentoptions-svg-dispensed-icon"
          name="Dispensed"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: isExperimentOrAllPlatesDispensed ? 'Dispensed' : 'Mark as Dispensed',

      onClick: markExperimentAsDispensed,
      isDisabled: rmLite || isExperimentOrAllPlatesDispensed,
    },
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-experimentoptions-svg-exporttotextfile-icon"
          name="Export"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: 'Export to Text File',
      onClick: exportToTextScreen,
      isDisabled: rmLite,
    },
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-experimentoptions-svg-exporttoxml-icon"
          name="Export"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: 'Export to XML',
      onClick: exportToXml,
      isDisabled: rmLite,
    },
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-experimentoptions-svg-exportpoi-icon"
          name="Export"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: 'Export POI',
      onClick: exportPoi,
      isDisabled: rmLite,
    },
  ];

  const ScreenOptions: IDropDownList[] = [
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-screenoptions-svg-rotatecanvas-icon"
          name="RotateCanvas"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: 'Rotate Canvas',

      onClick: () =>
        rotateCanvas(
          selectedNodes,
          <RotateCanvasModal
            id="projectsidepanel-screenoptions-rotatecanvasmodal"
            onChange={onChangeRotateCanvasMessageBoxValue}
          />,
          updatedProperties,
          createNewNode,
        ),
      isDisabled: !selectedNodes.every((item) => item.type === 'Screen'),
    },
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-screenoptions-svg-screenstockreport-icon"
          name="ScreenStockReport"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: 'Screen Stock Report',

      onClick: licenseAuthCommand(() => {
        setModalStates({
          ...modalStates,
          showModal: true,
          isCancelOnly: false,
          body: 'Are you sure you want to generate Screen Stock Report?',
          heading: 'Confirmation',
          onOk: onGenerateScreenStocskReport,
          noButtonText: 'Cancel',
        });
      }, EUserAction.ScreenStockReport),
    },
    {
      Icon: (
        <RMSvgIcon
          id="projectsidepanel-screenoptions-svg-export-icon"
          name="Export"
          fill="rgba(49, 49, 49, 1)"
        />
      ),
      label: 'Export to XML',

      onClick: exportToXml,
      isDisabled: false,
    },
  ];

  const optionsArray = {
    Experiment: ExperimentOptions,
    Screen: ScreenOptions,
  };

  useEffect(() => {
    if (basePanelNode) {
      setBaseSidePanel(basePanelNode, selectedNodes);
    }
  }, [basePanelNode, selectedNodes, wellScoreSelectedParams, showProteinVariantModal]);

  useEffect(() => {
    const controller = new AbortController();

    if (experimentId > 0) {
      fetchExperimentData(controller.signal);
    }

    return () => controller.abort();
  }, [experimentId]);

  useEffect(() => {
    const fetchProperty = async () => {
      const result = (await gpService.getUserProperty(
        'ExperimentPlate.AllowBarcodeEdit',
      )) as BooleanPropertyDTO;
      setIsEditBarcodeAllowed(result.value);
    };
    fetchProperty();
    useFetchAllImagingSettingsProfiles();
  }, []);

  const setBasePanelProps = (tabItems, name, node) => {
    setSelectedBasePanelNode((prevState) => ({
      ...prevState,
      tabItems: tabItems,
      headerTitle: name,
      headerIconProps: loadSVGIconProps(node),
    }));
  };

  const onChangeRotateCanvasMessageBoxValue = async (e: any, isChecked, setIsChecked) => {
    const prop: BooleanPropertyDTO = new BooleanPropertyDTO({
      displayName: 'Show Rotate Canvas MessageBox',
      name: 'Screen.RotateCanvas.ShowMessageBox',
      categoryName: 'Misc',
      value: isChecked,
    });

    if (prop instanceof BooleanPropertyDTO) {
      prop.value = isChecked;
    }

    const idx = updatedProperties.findIndex((p) => p.name === prop.name);
    if (idx >= 0) {
      updatedProperties[idx] = prop;
    } else {
      updatedProperties.push(prop);
    }
    setIsChecked(!isChecked);
  };

  const setBaseSidePanel = useCallback(
    async (node, selectedNodes) => {
      const { type, name, id, extra } = node;
      const sameTypeNode = [
        ETreeNodeTypes.Protein,
        ETreeNodeTypes.Seed,
        ETreeNodeTypes.ProjectsFolder,
        ETreeNodeTypes.Screens,
        ETreeNodeTypes.Projects,
        ETreeNodeTypes.ScreensFolder,
        ETreeNodeTypes.ProjectFolder,
        ETreeNodeTypes.CrystalTrackv4,
        ETreeNodeTypes.Searches,
        ETreeNodeTypes.SearchesFolder,
      ];
      if (selectedNodes?.length > 1) {
        const tabItems: ITabItem[] = [
          {
            content: (
              <MultipleFilesPanel
                id="projectsidepanel-basesidepanel-multifilespanel"
                count={selectedNodes?.length}
              />
            ),
          },
        ];
        setBasePanelProps(tabItems, name, node);
      } else if (sameTypeNode.includes(type)) {
        const tabItems: ITabItem[] = [
          {
            content: await getTab(type, id),
          },
        ];
        setBasePanelProps(tabItems, name, node);
      } else if (type === ETreeNodeTypes.Experiment) {
        const content = await getTab(type, id, extra);
        const tabItems: ITabItem[] | {} = [
          ...(!rmLite
            ? [
                {
                  id: 'projectsidepanel-basesidepanel-experiment-topscoredimages-tabitem',
                  title: 'Top Scored Images',
                  content: content[2],
                  iconProps: { name: 'TopImagesIcon' },
                },
              ]
            : []),
          {
            id: 'projectsidepanel-basesidepanel-experiment-platepreview-tabitem',
            title: 'Plate Preview',
            content: content[1],
            iconProps: { name: 'Plate' },
          },
          {
            id: 'projectsidepanel-basesidepanel-experiment-information-tabitem',
            title: 'Experiment Information',
            content: content[0],
            iconProps: { name: 'Info' },
          },
        ];
        setBasePanelProps(tabItems, name, node);
      } else if (type === ETreeNodeTypes.Screen) {
        const content = await getTab(type, id);
        const tabItems: ITabItem[] = [
          {
            id: 'projectsidepanel-basesidepanel-screen-plateinformation-tabitem',
            title: 'Lots Information',
            content: content[1],
            iconProps: { name: 'Plate' },
          },
          {
            id: 'projectsidepanel-basesidepanel-screen-screeninformation-tabitem',
            title: 'Screen Information',
            content: content[0],
            iconProps: { name: 'Info' },
          },
        ];
        setBasePanelProps(tabItems, name, node);
      } else if (type === ETreeNodeTypes.Project) {
        const content = await getTab(type, id);
        const tabItems: ITabItem[] | {} = [
          ...(!rmLite
            ? [
                {
                  id: 'projectsidepanel-basesidepanel-project-topscoredimages-tabitem',
                  title: 'Top Scored Images',
                  content: content[2],
                  iconProps: { name: 'TopImagesIcon' },
                },
              ]
            : []),
          {
            id: 'projectsidepanel-basesidepanel-project-information-tabitem',
            title: 'Project Information',
            content: content[0],
            iconProps: { name: 'Info' },
          },
          ...(!rmLite
            ? [
                {
                  id: 'projectsidepanel-basesidepanel-project-crystalpreview-tabitem',
                  title: 'Crystal Preview',
                  content: content[1],
                  iconProps: { name: 'crystaltracking' },
                },
              ]
            : []),
        ];
        setBasePanelProps(tabItems, name, node);
      } else if (type === ETreeNodeTypes.FragmentScreen) {
        const content = await getTab(type, id);
        const tabItems: ITabItem[] = [
          {
            id: 'projectsidepanel-basesidepanel-fragmentscreen-information-tabitem',
            title: 'Fragment Information',
            content: content[0],
            iconProps: { name: 'Info' },
          },
          {
            id: 'projectsidepanel-basesidepanel-fragmentscreen-fragment-tabitem',
            title: 'Fragment',
            content: content[1],
            iconProps: { name: 'Fragment', subfolder: 'Fragment' },
          },
        ];
        setBasePanelProps(tabItems, name, node);
      } else if (type === ETreeNodeTypes.SearchAnalysis) {
        const tabItems: ITabItem[] = [
          {
            id: 'projectsidepanel-basesidepanel-searchanlysis-tabitem',
            content: await getTab(type, id),
          },
        ];
        setBasePanelProps(tabItems, name, node);
      } else if (selectedExplorerNode) {
        // if a node doesn't have anything to show in side panel
        // side panel content will be active explorer node's info
        const tabItems: ITabItem[] = [
          {
            id: 'projectsidepanel-basesidepanel-selectedexplorernodeinfo-tabitem',
            content: await getTab(selectedExplorerNode.type, selectedExplorerNode.id),
          },
        ];
        setBasePanelProps(tabItems, selectedExplorerNode.name, selectedExplorerNode);
      }
    },
    [
      basePanelNode,
      wellScoreSelectedParams,
      showProteinVariantModal,
      selectedExplorerNode,
    ],
  );

  const fetchExperimentData = useCallback(
    async (signal: AbortSignal) => {
      try {
        const expData =
          basePanelNode?.type === ETreeNodeTypes.Experiment
            ? await experimentService.getExperimentService(experimentId, false, signal)
            : await screenService.getScreenById(experimentId, signal);
        if (expData === null) {
          return;
        }
        setExperimentData(expData);
        setSelectedPlateList([]);
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch experiment data!', error);
        }
      }
    },
    [experimentId, basePanelNode],
  );

  const setShowModal = (showModal) => {
    setModalStates({ ...modalStates, showModal });
  };

  return (
    <ProjectSidePanelContext.Provider
      value={{
        selectedPlateList,
        setSelectedPlateList,
        onEditPlateBarcode,
      }}
    >
      <BaseSidePanel
        id="projectsidepanel-basesidepanel"
        tabItems={selectedBasePanelNode?.tabItems}
        headerTitle={selectedBasePanelNode?.headerTitle}
        headerIconProps={selectedBasePanelNode?.headerIconProps}
        actionsLIst={!rmLite ? optionsArray[basePanelNode?.type] : []}
      />
      <RMModal
        id="projectsidepanel-show-modal"
        {...modalStates}
        setShowModal={setShowModal}
      />

      {reportType !== '' && (
        <ExperimentReportsDialogue
          selectedWell={experimentData?.wellDetails}
          reportType={reportType}
          setReportType={setReportType}
          canvasRef={null}
          docData={experimentData}
          platePadding={GetPlatePadding()}
        />
      )}
      <LoadingModal showModal={isLoading} text={currentOperation} />
      {awaitableModalContent}
    </ProjectSidePanelContext.Provider>
  );
};
export default ProjectSidePanel;
